/**
 * ClaimCard — displays a single extracted + validated claim.
 *
 * States:
 *  pending / validating  →  spinner
 *  done (supported)      →  green left border + expandable reasoning
 *  done (unsupported)    →  yellow left border + expandable reasoning
 *  done (contradicted)   →  red left border + expandable reasoning
 *  error                 →  error indicator + message
 */

import React, { useState } from 'react';
import { Badge, Separator } from '@e14c/feathers';
import {
  ChevronDown,
  ChevronUp,
  CheckmarkFilled,
  CloseFilled,
  Warning,
  Error as ErrorIcon,
} from '@e14c/feathers/icons';
import type { ClaimCard as ClaimCardType } from '../types';

interface ClaimCardProps {
  claim: ClaimCardType;
}

function VerdictIcon({ claim }: { claim: ClaimCardType }) {
  if (
    claim.validationStatus === 'pending' ||
    claim.validationStatus === 'validating'
  ) {
    return (
      <span className="inline-block size-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
    );
  }
  if (claim.validationStatus === 'error') {
    return <ErrorIcon size={16} className="text-highlight-error" />;
  }
  switch (claim.verdict) {
    case 'supported':
      return <CheckmarkFilled size={16} className="text-highlight-success" />;
    case 'contradicted':
      return <CloseFilled size={16} className="text-highlight-error" />;
    case 'unsupported':
      return <Warning size={16} className="text-highlight-warning" />;
    default:
      return (
        <span className="inline-block size-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
      );
  }
}

function leftBorderClass(claim: ClaimCardType): string {
  if (claim.validationStatus !== 'done') return '';
  switch (claim.verdict) {
    case 'supported':
      return 'border-l-[3px] border-l-highlight-success';
    case 'unsupported':
      return 'border-l-[3px] border-l-highlight-warning';
    case 'contradicted':
      return 'border-l-[3px] border-l-highlight-error';
    default:
      return '';
  }
}

function sourceTypeLabel(sourceType: string): string {
  switch (sourceType) {
    case 'paper_index':
      return 'Literature index';
    case 'web_search':
      return 'Web search';
    default:
      return sourceType;
  }
}

export const ClaimCard: React.FC<ClaimCardProps> = ({ claim }) => {
  const [expanded, setExpanded] = useState(false);

  const isDone =
    claim.validationStatus === 'done' || claim.validationStatus === 'error';
  const accentClass = leftBorderClass(claim);

  const toggle = () => {
    if (isDone) setExpanded((v) => !v);
  };

  const confidenceLabel =
    claim.confidence != null
      ? `${Math.round(claim.confidence * 100)}% confidence`
      : null;

  return (
    <div
      className={`shrink-0 bg-background border border-border rounded-md overflow-hidden transition-colors ${accentClass}`}
    >
      {/* Header row — only this area toggles expand/collapse */}
      <div
        className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-muted/40"
        onClick={toggle}
        role="button"
        aria-expanded={expanded}
        tabIndex={isDone ? 0 : undefined}
        onKeyDown={(e) => {
          if (!isDone) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <div className="shrink-0 pt-0.5 flex items-center min-w-[20px]">
          <VerdictIcon claim={claim} />
        </div>

        <span className="flex-1 text-sm leading-snug text-foreground">
          {claim.text}
        </span>

        {isDone && (
          <div className="shrink-0 text-muted-foreground mt-0.5">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        )}
      </div>

      {/* Expanded panel — clicks here do not collapse the card */}
      {expanded && isDone && (
        <div className="px-3 pb-3 pt-1 bg-muted/40">
          {claim.validationStatus === 'error' ? (
            <p className="text-sm text-highlight-error">
              {claim.error ?? 'Validation failed'}
            </p>
          ) : (
            <>
              {/* Verdict badge + confidence */}
              <div className="flex items-center gap-2 mb-2">
                <Badge
                  variant={
                    claim.verdict === 'supported'
                      ? 'teal'
                      : claim.verdict === 'contradicted'
                        ? 'rose'
                        : 'orange'
                  }
                >
                  {claim.verdict?.toUpperCase()}
                </Badge>
                {confidenceLabel && (
                  <span className="text-xs text-muted-foreground">
                    {confidenceLabel}
                  </span>
                )}
                {claim.source_types_used.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    via{' '}
                    {claim.source_types_used
                      .map((sourceType) => sourceTypeLabel(sourceType))
                      .join(', ')}
                  </span>
                )}
              </div>

              {/* Reasoning */}
              {claim.reasoning && (
                <p className="text-sm leading-relaxed text-foreground/80 mb-2">
                  {claim.reasoning}
                </p>
              )}

              {/* Supporting evidence */}
              {claim.supporting_chunks.length > 0 && (
                <div className="mt-2">
                  <Separator className="mb-2" />
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                    Supporting evidence
                  </p>
                  {claim.supporting_chunks.map((chunk, i) => (
                    <div
                      key={i}
                      className="text-xs text-foreground/70 bg-background border border-border rounded px-2 py-1 mb-1"
                    >
                      <p>
                        {chunk.text.slice(0, 200)}
                        {chunk.text.length > 200 ? '…' : ''}
                      </p>
                      {chunk.url && (
                        <a
                          href={chunk.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary underline"
                        >
                          {chunk.source}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Contradicting evidence */}
              {claim.contradicting_chunks.length > 0 && (
                <div className="mt-2">
                  <Separator className="mb-2" />
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                    Contradicting evidence
                  </p>
                  {claim.contradicting_chunks.map((chunk, i) => (
                    <div
                      key={i}
                      className="text-xs text-foreground/70 bg-background border border-border rounded px-2 py-1 mb-1"
                    >
                      <p>
                        {chunk.text.slice(0, 200)}
                        {chunk.text.length > 200 ? '…' : ''}
                      </p>
                      {chunk.url && (
                        <a
                          href={chunk.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary underline"
                        >
                          {chunk.source}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
